import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import FormReducer from "../../utils/FormReducer";
import GroupShareTable from "./GroupShareTable"; // adjust path if needed
import LoadingSvg from "../loadingSvg/LoadingSvg";
import NewShareForm from "./NewShareForm";
import UserDao from "../../dao/UserDao";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/GroupShare.scss";

/**
 * @component GroupShareForm
 * @description Manages the sharing of grading criteria with colleagues. Supports creation, editing, and deletion of shares tied to a homeroom group and grading profile.
 *
 * @param {Object} props
 * @param {Function} dispatch
 * @param {number|string} gradingProfileId
 * @param {number|string|null} homeroomGroupId
 * @param {Object} selectedEpr
 * @param {Object} state
 */

const GroupShareForm = ({
    dispatch,
    gradingProfileId,
    homeroomGroupId: initialHomeroomGroupId,
    selectedEpr,
    state
}) => {
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [autoSuggest, setAutoSuggest] = useState(null);
    const [show, setShow] = useState(false);
    const [showAccordion, setShowAccordion] = useState(false);
    const [homeroomGroupId, setHomeroomGroupId] = useState(initialHomeroomGroupId);
    const [loader, setLoader] = useState(false);
    const [existingShares, setExistingShares] = useState([]);
    const [openAccordionKey, setOpenAccordionKey] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);

    const accordionRef = useRef(null);
    const accordionItemRefs = useRef({});

    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, schoolLevelTemplateDto, schoolYearDto, token } = globalState ?? {};

    const schoolLevelForCurrent =
        currentTimePeriodDto?.name === "1st Semester"
            ? schoolLevelTemplateDto?.firstSemester
            : schoolLevelTemplateDto?.secondSemester;

    const grade = schoolLevelForCurrent?.gradeToSubjects.find(
        (grade) => parseInt(grade.profileGradeId, 10) === parseInt(selectedEpr.grade, 10)
    );

    const selectedGroup = selectedEpr?.homeroomGroups?.find(
        (g) => parseInt(g.key, 10) === parseInt(homeroomGroupId, 10)
    );

    let groupTitle = "";

    if (selectedGroup) {
        const studentCount = selectedGroup.homeroomGroupStudents?.length || 0;
        if (selectedGroup.type === "WHOLE_CLASS") {
            groupTitle = `Whole Class – ${studentCount} students`;
        } else {
            groupTitle = `${selectedGroup.name} – ${studentCount} students`;
        }
    } else {
        groupTitle = `Whole Class – ${selectedEpr.gradingProfileStudents?.length || 0} students`;
    }

    const initialFormState = {
        filter: "",
        who: "",
        whoName: ""
    };
    const [formState, formDispatch] = useReducer(FormReducer, initialFormState);

    /**
     * Handles updating the input field for employee lookup.
     *
     * @param {Object} e - Event object from input change.
     */
    const handleChange = (e) => {
        const { value } = e.target;
        setShow(true);
        formDispatch({
            type: "text",
            field: "who",
            payload: value
        });
    };

    /**
     * Clears search and selected employee.
     */
    const handleClearSearch = () => {
        setSelectedEmployee(null);
        setShowAccordion(false);
        formDispatch({ type: "text", field: "who", payload: "" });
    };

    /**
     * Attempts to create a new WHOLE_CLASS homeroom group if one doesn't exist and returns its ID.
     *
     * @returns {Promise<number|null>} - The created group ID or null on failure.
     */
    const handleCreateGroup = async () => {
        const arrayOfStudents = selectedEpr.gradingProfileStudents.map((student) => ({
            key: null,
            studentNumber: student.studentNumber
        }));

        const options = {
            action: "createHomeroomGroup",
            gradingProfileId: selectedEpr.key,
            data: {
                key: null,
                name: `INTERNAL_${selectedEpr.gradeName}_Whole Class`,
                type: "WHOLE_CLASS",
                schoolYearKey: schoolYearDto.key,
                timePeriod: currentTimePeriodDto.key,
                ownerGradingProfile: selectedEpr.key,
                homeroomGroupStudents: arrayOfStudents
            },
            token
        };

        setLoader(true);
        try {
            const response = await ElementaryProgressReportDao(options);
            const updatedProfile = response?.data?.payload;
            if (updatedProfile) {
                const group = updatedProfile.homeroomGroups.find((g) => g.type === "WHOLE_CLASS");
                return group.key;
            }
        } catch (err) {
            toast.error("Failed to create group.");
        } finally {
            setLoader(false);
        }
    };

    /**
     * Submits a new share. Validates permissions, determines the group, and sends data to backend.
     *
     * @param {Object} tempShare - The temporary share object to submit.
     */
    const handleSubmit = async (tempShare) => {
        if (!tempShare) {
            toast.error("Something went wrong. No temporary share found.");
            return;
        }

        const gradingCriteriaShares = tempShare.gradingCriteriaShares.filter(
            (s) => s.permission === "VIEW" || s.permission === "EDIT"
        );

        if (gradingCriteriaShares.length === 0) {
            toast.error("You must select at least one subject to share as View or Edit.");
            return;
        }

        setLoader(true);

        let groupId = homeroomGroupId;
        if (!groupId) {
            const wholeClassGroup = selectedEpr.homeroomGroups?.find((g) => g.type === "WHOLE_CLASS");
            if (wholeClassGroup) {
                groupId = wholeClassGroup.key;
            } else {
                groupId = await handleCreateGroup();
                setHomeroomGroupId(groupId);
            }
        }

        if (!groupId) {
            toast.error("Could not determine a valid group to share.");
            setLoader(false);
            return;
        }

        const payload = {
            key: tempShare.key?.toString().startsWith("temp-") ? null : tempShare.key,
            homeroomGroupId: parseInt(groupId, 10),
            guid: parseInt(tempShare.guid, 10),
            gradingCriteriaShares: gradingCriteriaShares.map((s) => ({
                key: s.key ?? null,
                permission: s.permission,
                gradingCriteriaId: s.gradingCriteriaId
            }))
        };

        const options = {
            action: "updateGroupShare", // actually "create" but one call now
            gradingProfileId,
            homeroomGroupId: groupId,
            data: [payload],
            token
        };

        try {
            const response = await ElementaryProgressReportDao(options);

            if (!response?.data?.payload) {
                throw new Error("Missing payload in response");
            }

            const updatedProfile = response.data.payload;

            dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
            dispatch({
                type: "SET_PROFILES",
                payload: state.gradingProfiles.map((profile) =>
                    profile.key === updatedProfile.key ? updatedProfile : profile
                )
            });

            toast.success("Share created.");

            const newGroup = updatedProfile.homeroomGroups.find((g) => g.key.toString() === groupId.toString());

            const newShares =
                newGroup?.homeroomGroupShares?.filter((s) => s.guid === parseInt(tempShare.guid, 10)) || [];

            if (newShares.length > 0) {
                setExistingShares((prev) => {
                    const filtered = prev.filter(
                        (s) => s.key.toString() !== tempShare.key && s.guid !== tempShare.guid
                    );
                    return [...newShares, ...filtered];
                });
            }
            setTimeout(() => setOpenAccordionKey(null), 500);
            setSelectedEmployee(null);
            setShowAccordion(false);
        } catch (err) {
            toast.error("Failed to create share.");
        } finally {
            setLoader(false);
        }
    };

    /**
     * Sends updated shares to backend.
     *
     * @param {Object[]|Object} shares - One or more share objects to update.
     */
    const handleUpdateShare = async (shares) => {
        if (!Array.isArray(shares)) {
            shares = [shares];
        }

        const data = shares.map((share) => ({
            key: share.key,
            homeroomGroupId: parseInt(homeroomGroupId || share.homeroomGroupId, 10),
            guid: parseInt(share.guid, 10),
            gradingCriteriaShares: share.gradingCriteriaShares.map((gcs) => ({
                key: gcs.key ?? null,
                permission: gcs.permission,
                gradingCriteriaId: gcs.gradingCriteriaId
            }))
        }));

        const options = {
            action: "updateGroupShare",
            gradingProfileId: selectedEpr.key,
            homeroomGroupId: data[0].homeroomGroupId,
            data,
            token
        };

        setLoader(true);

        try {
            const response = await ElementaryProgressReportDao(options);
            const updatedProfile = response?.data?.payload;

            if (!updatedProfile) {
                throw new Error("Missing payload in response");
            }

            dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
            dispatch({
                type: "SET_PROFILES",
                payload: state.gradingProfiles.map((profile) =>
                    profile.key === updatedProfile.key ? updatedProfile : profile
                )
            });

            setOpenAccordionKey(null);
            toast.success("Shares updated.");
        } catch (err) {
            toast.error("Failed to update shares.");
        } finally {
            setLoader(false);
        }
    };

    /**
     * Sends a delete request for a share and updates UI if successful.
     *
     * @param {Object} share - The share object to delete.
     */
    const handleDeleteShare = async (share) => {
        const options = {
            action: "deleteHomeroomGroupShare",
            homeroomGroupShareId: share.key,
            token
        };

        setLoader(true);

        try {
            const response = await ElementaryProgressReportDao(options);

            if (response?.status === 200) {
                // Remove from local state
                setExistingShares((prev) => prev.filter((s) => s.key !== share.key));
                toast.success(`Share with ${share.displayName} deleted.`);
            } else {
                throw new Error("Delete request did not return status 200");
            }
        } catch (err) {
            toast.error("Failed to delete share.");
        } finally {
            setLoader(false);
        }
    };

    /**
     * Saves new and/or updated shares by dispatching to appropriate handlers.
     *
     * @param {Object[]|Object} shares - The shares to save.
     */
    const handleOnSave = (shares) => {
        const shareArray = Array.isArray(shares) ? shares : [shares];

        const newShares = shareArray.filter((s) => s.isNew);
        const existing = shareArray.filter((s) => !s.isNew);

        if (newShares.length > 0) {
            newShares.forEach((share) => handleSubmit(share));
        }

        if (existing.length > 0) {
            handleUpdateShare(existing);
        }
    };

    /**
     * Updates permission for a specific share and criteria combination.
     *
     * @param {string|number} shareKey - Key of the share.
     * @param {string|number} criteriaKey - Key of the grading criteria.
     * @param {string} newValue - New permission value ("VIEW", "EDIT", etc.).
     */
    const handleExistingPermissionChange = (shareKey, criteriaKey, newValue) => {
        const parsedCriteriaKey = parseInt(criteriaKey, 10);

        setExistingShares((prevShares) =>
            prevShares.map((share) => {
                if (share.key !== shareKey) return share;

                const updated = [...share.gradingCriteriaShares];
                const existingIndex = updated.findIndex(
                    (gcs) => parseInt(gcs.gradingCriteriaId, 10) === parsedCriteriaKey
                );

                if (existingIndex !== -1) {
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        permission: newValue.toUpperCase()
                    };
                } else {
                    updated.push({
                        key: null,
                        gradingCriteriaId: parsedCriteriaKey,
                        permission: newValue.toUpperCase()
                    });
                }

                // Step 2: De-duplicate to ensure only one per gradingCriteriaId
                const uniqueMap = new Map();
                updated.forEach((gcs) => {
                    const key = parseInt(gcs.gradingCriteriaId, 10);
                    if (!uniqueMap.has(key)) {
                        uniqueMap.set(key, gcs);
                    }
                });

                const dedupedAndSorted = Array.from(uniqueMap.values())
                    .filter((gcs) => gcs.permission !== "NONE")
                    .sort((a, b) => parseInt(a.gradingCriteriaId, 10) - parseInt(b.gradingCriteriaId, 10));

                return {
                    ...share,
                    gradingCriteriaShares: dedupedAndSorted
                };
            })
        );
    };

    /**
     * Resets homeroomGroupId state when prop changes.
     */
    useEffect(() => {
        setHomeroomGroupId(initialHomeroomGroupId);
    }, [initialHomeroomGroupId]);

    /**
     * Smooth scrolls to the open accordion when openAccordionKey is set.
     */
    useEffect(() => {
        if (openAccordionKey && accordionItemRefs.current[openAccordionKey]) {
            accordionItemRefs.current[openAccordionKey].scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
        }
    }, [openAccordionKey]);

    /**
     * Fetches employee suggestions based on `formState.who` (3+ characters, debounced).
     */
    useEffect(() => {
        const fetchEmployees = async (query) => {
            const options = {
                action: "userSummariesRead",
                params: {
                    role: "ROLE_EMPLOYEE",
                    statuses: "ACTIVE",
                    username: `${query}`
                },
                token
            };
            try {
                const response = await UserDao(options);
                const { payload, errors } = response?.data ?? {};
                if (payload) {
                    setAutoSuggest(payload);
                }
                if (errors?.length) {
                    toast.error("Something went wrong. Please try again later.");
                }
            } catch (err) {
                toast.error("Something went wrong. Please try again later.");
            }
        };

        const debounceTimer = setTimeout(() => {
            if (token && formState?.who?.length >= 3) {
                fetchEmployees(formState.who);
            } else {
                setAutoSuggest(null);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [formState.who, token]);

    /**
     * Scrolls to accordion when `showAccordion` is triggered.
     */
    useEffect(() => {
        if (showAccordion && accordionRef.current) {
            accordionRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [showAccordion]);

    /**
     * On load or group change, load existing shares and update state.
     */
    useEffect(() => {
        if (!selectedEpr) return;

        let group;

        if (homeroomGroupId) {
            group = selectedEpr.homeroomGroups?.find((g) => g.key.toString() === homeroomGroupId.toString());
        }

        if (!group) {
            group = selectedEpr.homeroomGroups?.find((g) => g.name.startsWith("INTERNAL_"));
            setHomeroomGroupId(group?.key);
        }

        const shares = group?.homeroomGroupShares || [];
        setExistingShares(shares);
    }, [selectedEpr, homeroomGroupId]);

    return (
        <main className="share-list">
            {existingShares.length > 0 && (
                <>
                    <div className="share-list-title">
                        <h5>Share List: {groupTitle}</h5>

                        <ActionButton
                            className="action-button-reg"
                            onClick={() => setShowShareModal(true)}
                            label="Create New Share"
                        />
                    </div>
                    <section className="share-form-wrapper">
                        <GroupShareTable
                            accordionItemRefs={accordionItemRefs}
                            existingShares={existingShares}
                            grade={grade}
                            handleDeleteShare={handleDeleteShare}
                            handleUpdateShare={handleUpdateShare}
                            selectedEpr={selectedEpr}
                            openAccordionKey={openAccordionKey}
                            onPermissionChange={handleExistingPermissionChange}
                            onSave={handleOnSave}
                            onDelete={handleDeleteShare}
                            setOpenAccordionKey={setOpenAccordionKey}
                        />
                    </section>
                </>
            )}
            <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Colleague to Share With</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <NewShareForm
                        autoSuggest={autoSuggest}
                        existingShares={existingShares}
                        formState={formState}
                        handleChange={handleChange}
                        handleClearSearch={handleClearSearch}
                        selectedEmployee={selectedEmployee}
                        setExistingShares={setExistingShares}
                        setOpenAccordionKey={setOpenAccordionKey}
                        setSelectedEmployee={setSelectedEmployee}
                        setShow={setShow}
                        setShowAccordion={setShowAccordion}
                        setShowShareModal={setShowShareModal}
                        show={show}
                        showAccordion={showAccordion}
                        isModal
                    />
                </Modal.Body>
            </Modal>
            {existingShares.length === 0 && (
                <>
                    <div className="create-new-form">
                        <h5>Create New Share: {groupTitle}</h5>
                    </div>
                    <NewShareForm
                        autoSuggest={autoSuggest}
                        existingShares={existingShares}
                        formState={formState}
                        handleChange={handleChange}
                        handleClearSearch={handleClearSearch}
                        selectedEmployee={selectedEmployee}
                        setExistingShares={setExistingShares}
                        setOpenAccordionKey={setOpenAccordionKey}
                        setSelectedEmployee={setSelectedEmployee}
                        setShow={setShow}
                        setShowAccordion={setShowAccordion}
                        show={show}
                        showAccordion={showAccordion}
                    />
                </>
            )}
            {loader && <LoadingSvg />}
        </main>
    );
};

GroupShareForm.propTypes = {
    dispatch: PropTypes.func.isRequired,
    gradingProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    homeroomGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedEpr: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        gradeName: PropTypes.string,
        homeroomGroups: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                name: PropTypes.string,
                type: PropTypes.string,
                homeroomGroupStudents: PropTypes.array,
                homeroomGroupShares: PropTypes.array
            })
        ),
        gradingCriteriaList: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                parentTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
            })
        ),
        gradingProfileStudents: PropTypes.arrayOf(
            PropTypes.shape({
                studentNumber: PropTypes.string.isRequired
            })
        )
    }).isRequired,
    state: PropTypes.shape({
        gradingProfiles: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
            })
        ).isRequired
    }).isRequired
};

GroupShareForm.defaultProps = {
    homeroomGroupId: null
};

export default GroupShareForm;
